import React from 'react';
import { Helmet } from 'react-helmet';
import '../Blog.css';

const BlogPost = () => {
    return (
        <div className="blog-container">
            <Helmet>
                <title>Two Years at CSE, IITH - A Journey of Growth and Discovery</title>
                <meta name="description" content="A reflective blog by Kritik Agarwal on his M.Tech journey at the Department of Computer Science and Engineering, IIT Hyderabad, with insights on academics, faculty, leadership, and personal growth." />
                <meta name="keywords" content="Kritik Agarwal, M.Tech, IIT Hyderabad, CSE, Computer Science Engineering, prAkshepan, academic journey, leadership, volunteering, machine learning, cybersecurity, personal growth" />
                <meta name="author" content="Kritik Agarwal" />
                <meta property="og:title" content="Two Years at CSE, IITH - A Journey of Growth and Discovery" />
                <meta property="og:description" content="A reflective blog by Kritik Agarwal on his M.Tech journey at IIT Hyderabad, sharing experiences on academics, leadership, and personal development." />
                <meta property="og:image" content="/cseiith.jpg" />
                <meta property="og:url" content="https://agarwalkritik.pages.dev/#/blog/mtechcse@iith" />
                <meta property="og:type" content="article" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Two Years at CSE, IITH - A Journey of Growth and Discovery" />
                <meta name="twitter:description" content="A reflective blog by Kritik Agarwal on his M.Tech journey at IIT Hyderabad, highlighting key experiences in academics, leadership, and personal growth." />
                <meta name="twitter:image" content="/cseiith.jpg" />
            </Helmet>

            <div className="blog-hero">
                <img src="/cseiith.jpg" alt="View of IIT Hyderabad campus" className="blog-hero-image" />
                <div className="blog-hero-text">
                    <h1><strong>Two Years at CSE, IITH</strong></h1>
                    <h2>A Journey of Growth and Discovery</h2>
                </div>
            </div>

            <div className="blog-header">
                <div className="blog-meta">
                    <span className="blog-date">April 6, 2025</span>
                    <span className="blog-author">by Kritik Agarwal, M.Tech CSE, IIT Hyderabad</span>
                </div>
            </div>

            <div className="blog-content">
                <section className="blog-section intro">
                    <p className="reflection-quote" align="center">
                        <strong>"It's not just what you learn, but who you become in the process that truly matters." </strong>

                    </p>

                    <p>
                        Standing at the threshold of completing my journey at the Department of Computer Science and Engineering (CSE)
                        at IIT Hyderabad, I find myself looking back at two extraordinary years that transformed not just my
                        technical skills, but my entire outlook on life and learning. Every challenge overcome, every
                        late-night study session, every moment of discovery – they've all woven together to create
                        an experience that has fundamentally reshaped who I am.
                    </p>
                </section>

                <section className="blog-section">
                    <h2>The Academic Expedition</h2>
                    <p>
                        When I joined the M.Tech program in CSE with a focus on Computer Science Engineering and Cybersecurity,
                        I had no idea how deeply this experience would change me. The curriculum wasn't just rigorous – it was
                        revelatory. Each course opened doors to new possibilities, pushing me beyond what I thought possible.
                    </p>

                    <p>
                        The blend of theoretical foundations and hands-on applications created a learning environment that demanded
                        not just memorization, but true understanding. From the elegant complexity of Advanced Data Structures to the
                        fascinating possibilities of Machine Learning, every subject became a stepping stone toward mastery.
                    </p>

                    <p>
                        I still remember my first network security project – the frustration of debugging until 3 AM, the
                        exhilaration when the system finally worked, and the pride of presenting findings that even impressed
                        my professors. These weren't just assignments; they were opportunities to apply abstract concepts to
                        real-world problems, deepening my understanding and igniting curiosity that extended far beyond the classroom.
                    </p>
                </section>

                <section className="blog-section">
                    <h2>Faculty Who Inspired and Challenged</h2>
                    <p>
                        At IITH, professors aren't just lecturers – they're mentors who genuinely invest in your growth. Their doors
                        are always open, their minds always curious, and their expectations always high. They don't just teach you
                        what to think; they teach you how to think.
                    </p>

                    <h3>Mentors Who Shaped My Path</h3>
                    <div className="mentor-gallery">
                        <div className="mentor-card">
                            <strong><a href="https://iith.ac.in/cse/tbr/">Prof. Bheemarjuna Reddy Tamma</a></strong>
                            <p>
                                A guiding light throughout my academic journey whose passion for computer networks and cybersecurity
                                transformed these subjects from course material to calling. His Advanced Computer Networks and Network
                                Security courses weren't just about protocols and defenses – they were about understanding the very
                                fabric of digital communication and protection. His teaching transcended textbooks, encouraging us to
                                question, explore, and innovate.
                            </p>
                        </div>

                        <div className="mentor-card">
                            <strong><a href="https://www.iith.ac.in/cse/shirshendu/">Prof. Shirshendu Das</a></strong> and
                            <strong><a href="https://www.iith.ac.in/cse/mvp/"> Prof. MV Panduranga Rao</a></strong>
                            <p>
                                Working as a Teaching Assistant for their DBMS-II and Advanced DSA courses respectively gave me new
                                perspectives on these subjects. They are not just professors; they are pioneers in their fields,
                                constantly pushing the boundaries of knowledge. They helped me beyond the academic curriculum, guiding me
                                to think critically and approach problems from multiple angles. Their mentorship taught me that the
                                best solutions often come from collaboration and diverse perspectives.
                            </p>
                        </div>

                        <div className="mentor-card">
                            <strong><a href="https://www.iith.ac.in/cse/saurabhkr/">Prof. Saurabh Kumar</a></strong>
                            <p>
                                As his TA for Network Security, I've learned that security isn't just about defense mechanisms –
                                it's about thinking like both protector and attacker, understanding vulnerabilities at their core,
                                and building systems that are resilient by design.
                            </p>
                        </div>

                        <div className="mentor-card">
                            <strong><a href="https://www.iith.ac.in/cse/sobhan/">Prof. Sobhan Babu Chintapalli</a></strong>
                            <p>
                                My thesis guide whose patient guidance has been invaluable. He taught me that research isn't a
                                linear path but a creative exploration, where roadblocks often lead to the most interesting discoveries.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="blog-section">
                    <h2>Beyond Academics: Leadership and Community</h2>

                    <h3>prAkshepan 2.0: Creating Legacy Through Leadership</h3>
                    <p>
                        Leading <em>prAkshepan 2.0</em> – our department's fresher orientation program – taught me more about
                        leadership than any textbook ever could. As Lead Organizer, I faced the challenge of welcoming new
                        students not just to a department, but to a community of scholars and innovators.
                    </p>

                    <p>
                        We designed interactive sessions where PhDs and Major K R Anuraj shared not just academic knowledge but personal journeys.
                        We organized quiz competitions that sparked friendly rivalries and fostered camaraderie has they won gifts from our side.
                        We created mentorship rings where seniors could share hard-won wisdom about everything from course
                        selection to finding the best coffee spots on campus.
                    </p>

                    <p>
                        What began as an orientation evolved into something more meaningful – a tradition that embodies the
                        collaborative spirit of our department. Seeing freshers transform from nervous newcomers to confident
                        members of our community within days was among my most rewarding experiences.
                    </p>

                    <p>
                        <a
                            href="https://www.linkedin.com/posts/agarwalkritik_prakshepan-departmentofcse-cse-activity-7225824357875335169-4Ue1?utm_source=share&utm_medium=member_desktop&rcm=ACoAACMnPCEBpVbc1Injp4ZcOI6DPAdS_MsCLGA"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-link"
                        >
                            📎 View the prAkshepan 2.0 Initiative on LinkedIn
                        </a>
                    </p>
                </section>

                <section className="blog-section">
                    <h2>The Human Element: Friendships That Transformed Me</h2>
                    <p>
                        The true magic of IITH lies in its people – a diverse tapestry of backgrounds, perspectives, and dreams.
                        Late-night debugging sessions somehow transformed into philosophical discussions about the ethics of AI.
                        Quick coffee breaks evolved into brainstorming sessions for potential startups. Casual walks around campus
                        became opportunities to exchange ideas that would later influence research directions.
                    </p>

                    <p>
                        I remember one particularly challenging group project when our team hit a seemingly insurmountable obstacle.
                        Instead of fracturing under pressure, we pulled an all-nighter – not just coding, but supporting each other,
                        challenging assumptions, and ultimately finding an elegant solution none of us could have discovered alone.
                        These moments taught me that the most innovative solutions emerge from collaboration across different ways of thinking.
                    </p>

                    <p>
                        The diversity at IITH exposed me to perspectives I'd never encountered before. Conversations with classmates
                        from different regions of India and abroad broadened my understanding of how technology impacts various communities.
                        These interactions weren't just personally enriching – they made me a better computer scientist by helping me see
                        beyond my own limited perspective.
                    </p>
                </section>

                <section className="blog-section">
                    <h2>Gratitude: Acknowledging the Pillars of My Journey</h2>
                    <h3>Bonds That Will Last a Lifetime</h3>
                    <p>
                        As I reflect on my journey, I am overwhelmed with gratitude for the friendships that have shaped my experience.
                        The bonds forged during study sessions, group projects, and shared laughter have become an integral part of my life.
                        These friendships have not only enriched my academic experience but have also created a deep sense of belonging that I will carry with me long after graduation.
                    </p>
                    <p>
                        <a href="https://in.linkedin.com/in/raghavendra-kulkarni-2128b81b8/">Raghavendra G Kulkarni</a> and I started as classmates, but over time, we became close friends and collaborators.
                        Our discussions—whether about machine learning, the latest tech trends, cricket matches, or life in general—not only deepened our understanding of these topics but also forged a bond that will last long after graduation.
                        Our shared passion for technology and innovation led us to explore new ideas, challenge each other's perspectives, and grow as individuals.
                        I am grateful for the countless hours spent brainstorming, coding, and laughing together. These experiences have enriched my academic journey and created memories I will cherish forever.
                    </p>

                    <p>
                        Alongside Raghavendra, some of my closest friends—<a href="https://www.linkedin.com/in/keshvi-dharaiya/">Keshvi Dharaiya</a>, <a href="https://www.linkedin.com/in/dipesh-mishra/">Dipesh Mishra</a>, and <a href="https://www.linkedin.com/in/khomesh-kumar-sahu/">Khomesh Kumar Sahu</a>—have been my pillars of support.
                        They have stood by me through the highs and lows, celebrating successes and offering comfort during setbacks.
                        Their unwavering belief in my abilities has inspired me to push my limits and strive for excellence.
                        I’m truly thankful for the many hours spent studying, collaborating, and laughing together. These friendships have not only enhanced my academic experience but have also created a deep sense of belonging that I will carry with me long after graduation.
                    </p>

                    <p>
                        I would also like to express my heartfelt gratitude to my seniors, <a href="https://www.linkedin.com/in/vishesh-kothari/">Vishesh Kothari</a> and <a href="https://www.linkedin.com/in/girish-thatte/">Girish Thatte</a>, whose support and mentorship were invaluable throughout my journey.
                        Their guidance during challenging times, encouragement when I needed it most, and willingness to share their knowledge and experiences helped me navigate both academic and personal hurdles with confidence.
                        I feel incredibly fortunate to have had such thoughtful and inspiring seniors to look up to.
                    </p>
                </section>

                <section className="blog-section">
                    <h2>Personal Growth: Becoming More Than a Technologist</h2>
                    <p>
                        My time at IITH wasn't just about becoming a better computer scientist – it was about becoming a more
                        complete person. The competitive yet supportive environment pushed me beyond my comfort zone daily,
                        teaching resilience that transcends technical skills.
                    </p>

                    <p>
                        I discovered strengths I never knew I had – like the ability to explain complex concepts in simple terms,
                        or to find common ground between conflicting viewpoints. I confronted weaknesses I'd previously ignored,
                        like my tendency to overthink problems rather than testing practical solutions.
                    </p>

                    <p>
                        Most importantly, I learned to embrace uncertainty. In technology, the only constant is change – and IITH
                        prepared me not just with current knowledge, but with the adaptability to thrive in a rapidly evolving field.
                    </p>
                </section>

                <section className="blog-section">
                    <h2>To Future Students & Juniors: Advice from the Other Side</h2>
                    <p>
                        To every student just beginning their IITH journey – this place will give you exactly what you put into it.
                        Don't just attend classes; engage with them. Don't just meet people; connect with them. Don't just complete
                        assignments; be transformed by them.
                    </p>

                    <div className="advice-box">
                        <h4>Three Principles That Served Me Well:</h4>
                        <ol>
                            <li>
                                <strong>Curiosity before grades.</strong> The questions you ask matter more than the answers you memorize.
                                The professors here respond to genuine curiosity with generosity – take advantage of that.
                            </li>
                            <li>
                                <strong>Collaboration over competition.</strong> Yes, you're surrounded by brilliant minds, but they're
                                potential collaborators, not competitors. Your classmates will become your professional network – invest in those relationships.
                            </li>
                            <li>
                                <strong>Balance is essential.</strong> All-nighters might get you through a project, but sustainable
                                success requires taking care of your mind and body. Find your rhythm between work and rest.
                            </li>
                        </ol>
                    </div>

                    <p>
                        Remember that you're not just passing through IITH – you're becoming part of its legacy. Every project you complete,
                        every initiative you launch, every connection you make contributes to the culture that future students will inherit.
                        Make it count.
                    </p>
                </section>

                <section className="blog-section conclusion">
                    <h2>Looking Back, Moving Forward</h2>
                    <p>
                        As I prepare to leave these familiar corridors, I carry with me far more than a degree. I take with me a
                        mindset shaped by rigorous inquiry, relationships forged through shared challenges, and memories that will
                        continue to inspire me long after graduation.
                    </p>

                    <p>
                        To IITH – thank you for being not just an institution, but a transformative experience. To my professors –
                        thank you for demanding excellence while providing the support to achieve it. To my friends and classmates –
                        thank you for making this journey as enriching personally as it was academically.
                    </p>

                    <p>
                        The lessons learned here will guide me through whatever challenges lie ahead. As one chapter ends, another
                        begins – but the story that started at IITH will continue to unfold throughout my life and career.
                    </p>

                    <p className="signature">
                        With gratitude and excitement for what's next,<br />
                        Kritik Agarwal
                    </p>

                    <p className="opening-quote" align="center">
                        <strong>"The only limit to our realization of tomorrow will be our doubts of today."</strong><br />
                        <strong>- Franklin D. Roosevelt</strong>
                    </p>
                </section>
            </div>
        </div>
    );
};

export default BlogPost;